import React, { useContext, useEffect } from 'react';
import {
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Rate,
  Row,
  Select,
  Slider,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import AccountPlanContext from '@totem/components/accountPlan/AccountPlanContext';
import {
  AccountPlan,
  ActionItem,
  EditSection,
  Opportunity,
  QBRRecord,
  Stakeholder,
} from '@totem/components/accountPlan/types';
import {
  DECISION_ROLES,
  newId,
  PRIORITY_LABELS,
  RELATIONSHIPS,
  STAGE_LABELS,
  STATUS_LABELS,
  subordinateIds,
  TIERS,
} from '@totem/components/accountPlan/utilities';

const TITLES: Record<EditSection, string> = {
  setup: 'Account setup',
  strategy: 'Strategy & goals',
  opportunity: 'Opportunity',
  stakeholder: 'Stakeholder',
  action: 'Action item',
  qbr: 'QBR record',
};

const toOptions = (labels: Record<string, string>) =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

const toDay = (value?: string) => (value ? dayjs(value) : undefined);
const fromDay = (value?: Dayjs | null) =>
  value ? value.startOf('day').toISOString() : undefined;
const toLines = (items: string[]) => items.join('\n');
const fromLines = (text?: string) =>
  (text ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

// Builds form initial values for the section being edited.
const initialValues = (
  plan: AccountPlan,
  section: EditSection,
  itemId?: string,
  seed?: object,
  ticketId?: string,
) => {
  switch (section) {
    case 'setup':
      return { ...plan.profile, ...plan.weights };
    case 'strategy':
      return {
        goal: plan.strategy.goal,
        competitive: plan.strategy.competitive,
        risks: toLines(plan.strategy.risks),
        priorities: toLines(plan.strategy.priorities),
      };
    case 'opportunity': {
      const item = plan.opportunities.find(
        (opportunity) => opportunity.id === itemId,
      );
      return item
        ? { ...item, closeDate: toDay(item.closeDate) }
        : { stage: 'qualifying', confidence: 25, revType: 'Recurring (ARR)' };
    }
    case 'stakeholder': {
      const item = plan.stakeholders.find(
        (stakeholder) => stakeholder.id === itemId,
      );
      return item
        ? { ...item, lastContacted: toDay(item.lastContacted) }
        : {
            relationship: 'Unknown',
            sentiment: 'neutral',
            influence: 3,
            orgType: 'Customer',
            ...seed,
          };
    }
    case 'action': {
      const item = plan.actions.find((action) => action.id === itemId);
      return item
        ? {
            ...item,
            dateAssigned: toDay(item.dateAssigned),
            dateDue: toDay(item.dateDue),
          }
        : {
            priority: 'medium',
            status: 'notStarted',
            percentComplete: 0,
            dateAssigned: dayjs(),
            ...seed,
          };
    }
    case 'qbr': {
      const item = plan.qbr.records.find((record) => record.id === itemId);
      return {
        cadence: plan.qbr.cadence,
        ...(item
          ? { ...item, date: toDay(item.date) }
          : { ticketId: ticketId ?? '' }),
      };
    }
    default:
      return {};
  }
};

const upsert = <T extends { id: string }>(items: T[], item: T) =>
  items.some((existing) => existing.id === item.id)
    ? items.map((existing) => (existing.id === item.id ? item : existing))
    : [...items, item];

// Applies the submitted form values back onto a copy of the plan.

const applyValues = (
  plan: AccountPlan,
  section: EditSection,
  itemId: string | undefined,
  values: any,
): AccountPlan => {
  switch (section) {
    case 'setup':
      return {
        ...plan,
        profile: {
          tier: values.tier ?? '',
          segment: values.segment ?? '',
          region: values.region ?? '',
          oneLiner: values.oneLiner ?? '',
        },
        weights: {
          operational: values.operational,
          commercial: values.commercial,
          relationship: values.relationship,
        },
      };
    case 'strategy':
      return {
        ...plan,
        strategy: {
          goal: values.goal ?? '',
          competitive: values.competitive ?? '',
          risks: fromLines(values.risks),
          priorities: fromLines(values.priorities),
        },
      };
    case 'opportunity': {
      const item: Opportunity = {
        notes: '',
        owner: '',
        service: '',
        building: '',
        revType: '',
        ...values,
        id: itemId ?? newId(),
        amount: values.amount ?? undefined,
        confidence: values.confidence ?? 0,
        closeDate: fromDay(values.closeDate),
      };
      return { ...plan, opportunities: upsert(plan.opportunities, item) };
    }
    case 'stakeholder': {
      const item: Stakeholder = {
        role: '',
        orgType: '',
        email: '',
        phone: '',
        decisionRole: '',
        notes: '',
        ...values,
        id: itemId ?? newId(),
        influence: values.influence ?? 0,
        lastContacted: fromDay(values.lastContacted),
        reportsTo: values.reportsTo ?? '',
      };
      return { ...plan, stakeholders: upsert(plan.stakeholders, item) };
    }
    case 'action': {
      const existing = plan.actions.find((action) => action.id === itemId);
      const item: ActionItem = {
        assignedTo: '',
        notes: '',
        linked: '',
        ...values,
        id: itemId ?? newId(),
        source: existing?.source ?? 'manual',
        percentComplete:
          values.status === 'complete' ? 100 : (values.percentComplete ?? 0),
        dateAssigned: fromDay(values.dateAssigned),
        dateDue: fromDay(values.dateDue),
      };
      return { ...plan, actions: upsert(plan.actions, item) };
    }
    case 'qbr': {
      const { cadence, ...record } = values;
      const hasRecord =
        record.title ||
        record.outcomes ||
        record.theme ||
        record.attendees ||
        record.date;
      const item: QBRRecord = {
        title: '',
        ticketId: '',
        attendees: '',
        theme: '',
        outcomes: '',
        ...record,
        id: itemId ?? newId(),
        date: fromDay(record.date),
      };
      return {
        ...plan,
        qbr: {
          cadence: cadence ?? '',
          records: hasRecord
            ? upsert(plan.qbr.records, item)
            : plan.qbr.records,
        },
      };
    }
    default:
      return plan;
  }
};

const SetupFields = ({ ownersNote }: { ownersNote: string }) => (
  <>
    <Row gutter={12}>
      <Col span={12}>
        <Form.Item name="tier" label="Tier">
          <Select
            allowClear
            options={TIERS.map((tier) => ({ value: tier, label: tier }))}
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name="segment" label="Segment">
          <Input placeholder="e.g. Commercial Office" />
        </Form.Item>
      </Col>
    </Row>
    <Form.Item name="region" label="Region">
      <Input placeholder="e.g. FL / NC" />
    </Form.Item>
    <Form.Item name="oneLiner" label="Account one-liner">
      <Input.TextArea autoSize={{ minRows: 2 }} />
    </Form.Item>
    <Typography.Text type="secondary">{ownersNote}</Typography.Text>
    <Typography.Title level={5} style={{ marginTop: 16 }}>
      Health weights (must total 100)
    </Typography.Title>
    <Row gutter={12}>
      {['operational', 'commercial', 'relationship'].map((key) => (
        <Col span={8} key={key}>
          <Form.Item
            name={key}
            label={key[0].toUpperCase() + key.slice(1)}
            rules={[{ required: true }]}
          >
            <InputNumber
              min={0}
              max={100}
              addonAfter="%"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      ))}
    </Row>
    <Form.Item shouldUpdate noStyle>
      {({ getFieldValue }) => {
        const total =
          (getFieldValue('operational') ?? 0) +
          (getFieldValue('commercial') ?? 0) +
          (getFieldValue('relationship') ?? 0);
        return total === 100 ? null : (
          <Typography.Text type="danger">
            Weights total {total} — must be 100.
          </Typography.Text>
        );
      }}
    </Form.Item>
  </>
);

const StrategyFields = () => (
  <>
    <Form.Item name="goal" label="Where we want this account in 12 months">
      <Input.TextArea autoSize={{ minRows: 3 }} />
    </Form.Item>
    <Form.Item name="risks" label="Key risks / watch-outs (one per line)">
      <Input.TextArea autoSize={{ minRows: 3 }} />
    </Form.Item>
    <Form.Item
      name="priorities"
      label="Strategic priorities this quarter (one per line)"
    >
      <Input.TextArea autoSize={{ minRows: 3 }} />
    </Form.Item>
    <Form.Item name="competitive" label="Competitive landscape">
      <Input.TextArea autoSize={{ minRows: 2 }} />
    </Form.Item>
  </>
);

const OpportunityFields = () => (
  <>
    <Form.Item
      name="title"
      label="Opportunity"
      rules={[{ required: true, message: 'Title is required' }]}
    >
      <Input />
    </Form.Item>
    <Row gutter={12}>
      <Col span={8}>
        <Form.Item name="amount" label="Amount">
          <InputNumber
            min={0}
            prefix="$"
            style={{ width: '100%' }}
            placeholder="TBD"
          />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item name="revType" label="Revenue type">
          <Select
            options={['Recurring (ARR)', 'One-time', 'Mixed'].map((type) => ({
              value: type,
              label: type,
            }))}
          />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item name="stage" label="Stage">
          <Select options={toOptions(STAGE_LABELS)} />
        </Form.Item>
      </Col>
    </Row>
    <Row gutter={12}>
      <Col span={8}>
        <Form.Item name="confidence" label="Confidence %">
          <Slider min={0} max={100} step={5} />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item name="closeDate" label="Expected close">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item name="owner" label="Owner">
          <Input />
        </Form.Item>
      </Col>
    </Row>
    <Row gutter={12}>
      <Col span={12}>
        <Form.Item name="service" label="Service">
          <Input />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name="building" label="Building">
          <Input />
        </Form.Item>
      </Col>
    </Row>
    <Form.Item name="notes" label="Notes">
      <Input.TextArea autoSize={{ minRows: 2 }} />
    </Form.Item>
  </>
);

type StakeholderFieldsProps = {
  stakeholders: Stakeholder[];
  itemId?: string;
};

const StakeholderFields = ({
  stakeholders,
  itemId,
}: StakeholderFieldsProps) => {
  // A stakeholder can't report to themselves or to anyone below them.
  const excluded = itemId ? subordinateIds(stakeholders, itemId) : new Set();
  const managerOptions = stakeholders
    .filter(
      (stakeholder) =>
        stakeholder.id !== itemId && !excluded.has(stakeholder.id),
    )
    .map((stakeholder) => ({
      value: stakeholder.id,
      label: stakeholder.role
        ? `${stakeholder.name} (${stakeholder.role})`
        : stakeholder.name,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));

  return (
    <>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="role" label="Role / title">
            <Input />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="reportsTo"
            label="Reports to"
            tooltip="Pick from stakeholders already on this plan. Add the manager first if they're missing."
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Top level"
              options={managerOptions}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="orgType" label="Organization type">
            <Select
              options={['Customer', 'Property management', 'Vendor'].map(
                (type) => ({ value: type, label: type }),
              )}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="email" label="Email">
            <Input />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="relationship" label="Relationship">
            <Select
              options={RELATIONSHIPS.map((relationship) => ({
                value: relationship,
                label: relationship,
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="decisionRole" label="Decision role">
            <Select
              allowClear
              options={DECISION_ROLES.map((role) => ({
                value: role,
                label: role,
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="sentiment" label="Sentiment">
            <Select
              options={[
                { value: 'positive', label: '😊 Positive' },
                { value: 'neutral', label: '😐 Neutral' },
                { value: 'negative', label: '☹️ Negative' },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="influence" label="Influence">
            <Rate count={5} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="lastContacted" label="Last contacted">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="notes" label="Notes">
        <Input.TextArea autoSize={{ minRows: 2 }} />
      </Form.Item>
    </>
  );
};

const ActionFields = () => (
  <>
    <Form.Item
      name="action"
      label="Action"
      rules={[{ required: true, message: 'Action is required' }]}
    >
      <Input />
    </Form.Item>
    <Row gutter={12}>
      <Col span={8}>
        <Form.Item name="assignedTo" label="Assigned to">
          <Input />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item name="dateAssigned" label="Date assigned">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item name="dateDue" label="Date due">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
      </Col>
    </Row>
    <Row gutter={12}>
      <Col span={8}>
        <Form.Item name="priority" label="Priority">
          <Select options={toOptions(PRIORITY_LABELS)} />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item name="status" label="Status">
          <Select options={toOptions(STATUS_LABELS)} />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item name="percentComplete" label="% complete">
          <InputNumber
            min={0}
            max={100}
            addonAfter="%"
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Col>
    </Row>
    <Form.Item name="linked" label="Linked opportunity / ticket">
      <Input />
    </Form.Item>
    <Form.Item name="notes" label="Notes">
      <Input.TextArea autoSize={{ minRows: 2 }} />
    </Form.Item>
  </>
);

const QbrFields = () => (
  <>
    <Form.Item name="cadence" label="QBR cadence">
      <Select
        allowClear
        options={['Quarterly', 'Semi-annual', 'Annual', 'TBD'].map(
          (cadence) => ({ value: cadence, label: cadence }),
        )}
      />
    </Form.Item>
    <Typography.Title level={5}>QBR notes</Typography.Title>
    <Form.Item name="ticketId" hidden>
      <Input />
    </Form.Item>
    <Row gutter={12}>
      <Col span={16}>
        <Form.Item name="title" label="Title">
          <Input />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item name="date" label="Date held">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
      </Col>
    </Row>
    <Form.Item name="attendees" label="Attendees">
      <Input />
    </Form.Item>
    <Form.Item name="theme" label="Theme">
      <Input />
    </Form.Item>
    <Form.Item name="outcomes" label="Outcomes / decisions">
      <Input.TextArea autoSize={{ minRows: 3 }} />
    </Form.Item>
  </>
);

const AccountPlanEditor = () => {
  const { data, editTarget, closeEditor, savePlan, saving } =
    useContext(AccountPlanContext);
  const [form] = Form.useForm();

  useEffect(() => {
    if (editTarget && data) {
      form.resetFields();

      form.setFieldsValue(
        initialValues(
          data.plan,
          editTarget.section,
          editTarget.itemId,
          editTarget.seed,
          editTarget.ticketId,
        ),
      );
    }
  }, [editTarget, data, form]);

  if (!data || !editTarget) {
    return null;
  }

  const onSave = () =>
    form.validateFields().then((values) =>
      savePlan(
        applyValues(data.plan, editTarget.section, editTarget.itemId, values),
      ).then((saved) => {
        if (saved) {
          closeEditor();
        }
      }),
    );

  const isNewListItem =
    !editTarget.itemId &&
    ['opportunity', 'stakeholder', 'action'].includes(editTarget.section);
  const { owners } = data.summary;
  const ownersNote = `Account Manager (${owners.accountManager?.name ?? 'none'}), Engagement Manager and Sales Rep come from the organization's points of contact in the Portal.`;

  const fields: Record<EditSection, React.ReactNode> = {
    setup: <SetupFields ownersNote={ownersNote} />,
    strategy: <StrategyFields />,
    opportunity: <OpportunityFields />,
    stakeholder: (
      <StakeholderFields
        stakeholders={data.plan.stakeholders}
        itemId={editTarget.itemId}
      />
    ),
    action: <ActionFields />,
    qbr: <QbrFields />,
  };

  return (
    <Modal
      open
      width={720}
      title={`${isNewListItem ? 'Add' : 'Edit'} ${TITLES[editTarget.section].toLowerCase()}`}
      okText="Save"
      confirmLoading={saving}
      onOk={onSave}
      onCancel={closeEditor}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        {fields[editTarget.section]}
      </Form>
    </Modal>
  );
};

export default AccountPlanEditor;
